import VueRouter, { RouteConfig } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { getSubdomain } from '@/utils/subdomain-handler'

export const routes: RouteConfig[] = [
  {
    path: '/',
    redirect: () => getSubdomain() === 'vegace' ? '/search' : '/home',
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/home',
    component: () => import('@/views/home/index.vue'),
    name: 'Home',
    meta: {
      fullscreen: false
    },
    beforeEnter: (to, from, next) => {
      const userStore = useUserStore()
      if (
        userStore.isDualFlowEnabled
        || userStore.isBemoveCustomer
        || userStore.isAvantagesIEGCustomer
        || userStore.isVegaCECustomer
        || getSubdomain() === 'vegace'
      ) next('/search')
      else next()
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/sign-in/index.vue'),
    meta: {
      fullscreen: true
    }
  },
  {
    path: '/verification',
    name: 'Verification',
    component: () => import('@/views/verification/index.vue'),
    meta: {
      fullscreen: true,
      hideNavigation: true
    }
  },
  {
    path: '/sign-up',
    name: 'SignUp',
    component: () => import('@/views/sign-up/index.vue'),
    meta: {
      fullscreen: true,
      hideNavigation: true
    }
  },
  {
    path: '/confirmed-email',
    name: 'ConfirmedEmail',
    component: () => import('@/views/confirmed-email/index.vue'),
    meta: {
      fullscreen: true,
      hideNavigation: true
    }
  },
  {
    path: '/unconfirmed-email',
    name: 'UnconfirmedEmail',
    component: () => import('@/views/unconfirmed-email/index.vue'),
    meta: {
      fullscreen: true,
      hideNavigation: true
    }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/profile/index.vue'),
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: () => import('@/views/notifications-list/index.vue'),
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/orders',
    name: 'Orders',
    component: () => import('@/views/orders-list/index.vue'),
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/wallet/',
    component: () => import('@/views/wallet/index.vue'),
    props: true,
    meta: {
      fullscreen: false
    },
    children: [
      {
        path: '',
        redirect: '/wallet/ebons',
      },
      {
        path: 'ebons',
        component: () => import('@/views/wallet/components/PurchasedGiftCardsList.vue')
      },
      {
        path: 'multi-choice-cards',
        component: () => import('@/views/wallet/components/MultiChoiceCardsList.vue')
      }
    ]
  },
  {
    path: '/search',
    name: 'Search',
    component: () => import('@/views/search-giftcard/index.vue'),
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/search/discounts',
    name: 'SearchDiscounts',
    component: () => import('@/views/search-giftcard/index.vue'),
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/search/mcc/:id',
    name: 'SearchMCC',
    component: () => import('@/views/search-giftcard/index.vue'),
    props: true,
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/dotation/:mccId/products',
    name: 'DotationProducts',
    component: () => import('@/views/dotation/products.vue'),
    props: true,
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/dotation/:mccId/bank-card',
    name: 'PrepaidBankCard',
    component: () => import('@/views/dotation/prepaid-bank-card/index.vue'),
    props: true,
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/gift-cards/:id',
    name: 'GiftCardSheet',
    component: () => import('@/views/gift-card-sheet/index.vue'),
    props: true,
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/gift-cards/discounts/:id',
    name: 'GiftCardSheetDiscount',
    component: () => import('@/views/gift-card-sheet/index.vue'),
    props: true,
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/gift-cards/mcc/:id',
    name: 'GiftCardSheetMcc',
    component: () => import('@/views/gift-card-sheet/index.vue'),
    props: true,
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/wallet/:giftCardId/order-success/:orderId',
    name: 'OrderSuccessChoice',
    props: true,
    component: () => import('@/views/wallet/OrderSuccessChoice.vue'),
    meta: {
      fullscreen: false,
      hideNavigation: true
    }
  },
  {
    path: '/wallet/:giftCardId/orders/:orderId',
    name: 'WalletOrderCards',
    props: true,
    component: () => import('@/views/wallet/WalletOrderCards.vue'),
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/wallet/:id',
    name: 'WalletCards',
    props: true,
    component: () => import('@/components/WalletGiftCards/GiftCardsWalletDetails.vue'),
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/opt-in-refused',
    name: 'OptInRefused',
    component: () => import('@/views/opt-in-refused/index.vue'),
    meta: {
      fullscreen: true
    }
  },
  {
    path: '/lyfpay-transition',
    name: 'LyfpayTransition',
    component: () => import('@/views/lyfpay-transition/index.vue'),
    meta: {
      fullscreen: true,
      hideNavigation: true
    }
  },
  {
    path: '/cgv',
    name: 'CGV',
    component: () => import('@/components/TermsOfUse.vue'),
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/privacy',
    name: 'AvantagesIEGPrivacy',
    component: () => import('@/views/avantagesieg-privacy/index.vue'),
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/token-expired',
    name: 'TokenExpired',
    component: () => import('@/views/token-expired/index.vue'),
    meta: {
      fullscreen: true
    }
  },
  {
    path: '/locked',
    name: 'Locked',
    component: () => import('@/views/locked/index.vue'),
    meta: {
      fullscreen: true
    }
  },
  {
    path: '/sav',
    name: 'SAV',
    component: () => import('@/views/sav-kommunicate/index.vue'),
    meta: {
      fullscreen: false
    }
  },
  {
    path: '/payment-success',
    name: 'PaymentSuccess',
    component: () => import('@/views/payment-success.vue'),
    meta: {
      fullscreen: true,
      hideNavigation: true
    }
  },
  {
    path: '/payment-refused',
    name: 'PaymentRefused',
    component: () => import('@/views/payment-refused.vue'),
    meta: {
      fullscreen: true,
      hideNavigation: true
    }
  },
  {
    path: '*',
    redirect: () => getSubdomain() === 'vegace' ? '/search' : '/home'
  }
]

const SCROLL_TRANSITION_MS = 220

function scrollPageToTop(): void {
  window.scrollTo(0, 0)
  const scrollingElement = document.scrollingElement || document.documentElement
  scrollingElement.scrollTop = 0
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
}

const createRouter = () =>
  new VueRouter({
    mode: 'history',
    scrollBehavior: (_to, _from, savedPosition) => {
      return new Promise((resolve) => {
        setTimeout(() => {
          if (savedPosition) {
            resolve(savedPosition)
            return
          }
          scrollPageToTop()
          resolve({ x: 0, y: 0 })
        }, SCROLL_TRANSITION_MS)
      })
    },
    base: process.env.BASE_URL,
    routes: routes
  })

const router = createRouter()

router.onError((error: Error) => {
  const isChunkLoadError = /Loading chunk|Loading CSS chunk/.test(error.message || '')
  if (!isChunkLoadError) return

  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
  if (nav && nav.type === 'reload') return

  sessionStorage.setItem('walleo:splashShown', '1')
  window.location.reload()
})

export function resetRouter(): void {
  const newRouter: any = createRouter();
  (router as any).matcher = (newRouter as any).matcher
}

export default router
